import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
} from "reactstrap";
import icnzonalconfig from "../../../assets/images/icn_zonal_config.svg";
import boundriesicn from "../../../assets/images/boundries_icn.svg";
import {
  IFetchZonalConfigNormalizedResponse,
  IIdValuePairResponse,
  IZonalConfigListProps,
} from "../../../interfaces/Index";
import { _ } from "../../../utils/lib";
import ButtonComponent from "../../common/Button/ButtonComponent";
import DataTable from "../../common/DataTable/DataTable";
import Toaster from "../../common/Toaster/Toaster";
import LoaderComponent from "../../common/Loader/Loader";
import {
  headerData,
  bodyDataAndKeyTypes,
  footerData,
} from "./ZonalConfigsTableConstants";
import { Can } from "../../../config/Can";
import "./ZonalConfigsList.scss";
import SearchableSelect from "../../common/SearchableSelect/SearchableSelect";
import { normalizeBulkUploadPayload } from "normalizer/GlobalProductNormalizer";
import { ZONAL_CSV_BOUNDARY_UPLOAD_URL } from "config/Url";
import Modal from "components/common/Modal/Modal";
import UploadButtonComponent from "components/common/UploadButton/UploadButtonComponent";
import { acceptableFormats } from "config/constants";
import searchicon from "../../../assets/images/search-icon.svg";
import close from "../../../assets/images/close.svg";
import backarrow from "../../../assets/images/back_arrow.svg";
import warning from "../../../assets/images/warning.svg";

export const doAPIsearch = (
  val: any,
  pageScrollPosition: any,
  fetchZonalConfigs: any,
  cityId: any
) => {
  if (val.length >= 1) {
    pageScrollPosition.current = 0;
    fetchZonalConfigs(cityId, {
      searchKey: val,
    });
  }
};

const ZonalConfigsList: React.FunctionComponent<IZonalConfigListProps> = (
  props: IZonalConfigListProps
) => {
  let pageScrollPosition: any = useRef(props.currentListPosition);
  const [isSearch, setIsSearching] = useState(
    props.retainedSearchText ? true : false
  );
  const [searchTerm, setSearchTerm] = useState(props.retainedSearchText);
  const [isLoadMore, setLoadMore] = useState(false);
  const [loadMoreText, setLoadMoreText] = useState({
    text: "Load more...",
    isLoading: false,
  });
  const [zonalConfigsData, setData] = useState<
    Array<IFetchZonalConfigNormalizedResponse>
  >([]);
  const [selectedCity, setSelectedCity] = useState<IIdValuePairResponse>(
    props.selectedCity as any
  );
  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });
  const [batchModal, setBatchModal] = useState(false);
  const [showBatchModal, setIsShowBatchModal] = useState(false);
  const [showInventoryModal, setIsShowZonalConfigModal] = useState(false);

  useEffect(() => {
    const { fetchCities } = props;
    fetchCities();
  }, []);

  useEffect(() => {
    if (!selectedCity) {
      const newSelectedCity =
        props.citiesList && props.citiesList.length
          ? props.citiesList[0]
          : selectedCity;
      handleSelectedCityChange(newSelectedCity);
    }
  }, [props.citiesList, selectedCity?.id]);

  useEffect(() => {
    const { zonalConfigs, isZonalConfigsAddedorUpdated, fetchZonalConfigs } =
      props;

    if (zonalConfigs || isZonalConfigsAddedorUpdated) {
      if (selectedCity && selectedCity.id.length) {
        fetchZonalConfigs(selectedCity.id, {
          sortColumn: "ucName",
          sortDirection: "ASC",
        });
      }
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, pageScrollPosition.current);
    }
  }, [
    props.citiesList.length,
    props.isCityListFetched,
    selectedCity,
    props.currentLocation,
  ]);

  useEffect(() => {
    const { zonalConfigs, pagination } = props;
    if (zonalConfigs) {
      setData(zonalConfigs);
      setTimeout(() => {
        window.scrollTo(
          0,
          window.pageYOffset - (window.pageYOffset - pageScrollPosition.current)
        );
      }, 250);
      if (pagination) {
        setLoadMore(true);
        setLoadMoreText({
          text: "Load more...",
          isLoading: false,
        });
      } else {
        setLoadMore(false);
      }
    }
  }, [props.zonalConfigs]);

  useEffect(() => {
    const { error, isError } = props;
    if (isError && error) {
      alert((error! as any).errors);
    }
  }, [props.isError]);

  useEffect(() => {
    if (props.isZonalConfigsAdded) {
      const message = "ZonalConfig created successfully!";
      handleToaster(false, message);
    }
    if (props.isZonalConfigsUpdated) {
      const message = "ZonalConfig status updated successfully";
      handleToaster(false, message);
    } else {
      const { error, isError } = props;
      if (isError && error) {
        handleToaster(true, (error! as any).errors);
      }
    }
  }, [props.isZonalConfigsAdded, props.isZonalConfigsUpdated]);

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  const debounceSearch = useCallback(
    _.debounce(
      (value: any, cityId: any) =>
        doAPIsearch(value, pageScrollPosition, props.fetchZonalConfigs, cityId),
      1000
    ),
    []
  );

  const handleSearchInputChange = (e: any) => {
    const { fetchZonalConfigs } = props;
    const value = e.target.value;
    setSearchTerm(value);
    debounceSearch(value, selectedCity.id);

    if (value.length > 0 && !isSearch) {
      setIsSearching(true);
    }
    if (value.length === 0 && isSearch) {
      fetchZonalConfigs(selectedCity.id, {
        searchKey: value,
      });
    }
  };

  const addBatchModal = () => {
    setBatchModal(!batchModal);
  };

  const handleUploadZonalConfig = (file: any) => {
    setBatchModal(false);
    const { zonalConfigUpload } = props;
    const normalizedPayload = normalizeBulkUploadPayload(file);
    zonalConfigUpload(
      selectedCity.id,
      normalizedPayload,
      ZONAL_CSV_BOUNDARY_UPLOAD_URL
    );
    setIsShowBatchModal(false);
    setIsShowZonalConfigModal(true);
  };

  const handleUploadZonalConfigBatch = (file: any) => {
    setBatchModal(false);
    const { zonalCSVBatchUpload } = props;
    const normalizedPayload = normalizeBulkUploadPayload(file);
    zonalCSVBatchUpload(normalizedPayload);
    setIsShowBatchModal(false);
    setIsShowZonalConfigModal(true);
  };

  const closeModal = () => {
    setIsShowBatchModal(false);
    setIsShowZonalConfigModal(false);
    setBatchModal(false);
  };

  const editZonalConfig = (e: any, i: number) => {
    const { addUpdatePageHandler } = props;

    addUpdatePageHandler(
      selectedCity,
      true,
      zonalConfigsData[i],
      searchTerm,
      window.pageYOffset
    );
  };

  const closeSearch = () => {
    const { fetchZonalConfigs } = props;
    fetchZonalConfigs(selectedCity.id);
    setIsSearching(false);
    setSearchTerm("");
  };

  const handleSelectedCityChange = (e: IIdValuePairResponse) => {
    const { fetchZonalConfigs } = props;
    fetchZonalConfigs(e.id);
    setSelectedCity(e);
  };

  return (
    <div className="zonal-config-list-container">
      {props.isLoading && <LoaderComponent />}
      <Modal
        modalHeading="Add a batch"
        showModal={batchModal}
        closeModalHandler={closeModal}
        showProgressString={false}
        modalBodyClass="batch-modal"
      >
        <Row className="py-5">
          <Col>
            <div className="icn-container">
              <img className="upload_inventory_icon" src={icnzonalconfig} />
            </div>
            <UploadButtonComponent
              id="addUpdateZonalConfigBatch"
              buttonText="Upload Boundaries"
              fileFormat={acceptableFormats.csv}
              handleFileChange={handleUploadZonalConfig}
              classes="btn-primary btn button-medium upload-boundries"
            />
            <a
              href={require("../../../assets/static/csvUpdate.csv")}
              target="_blank"
              className="btn-primary-outline button-large btn btn-secondary no-border-btn"
            >
              Boundaries Sample File
            </a>
          </Col>
          <Col>
            <div className="icn-container">
              <img className="upload_inventory_icon" src={boundriesicn} />
            </div>
            <UploadButtonComponent
              id="addUpdateZonalConfigBatchServiceArea"
              buttonText="Upload Service Area"
              fileFormat={acceptableFormats.csv}
              handleFileChange={handleUploadZonalConfigBatch}
              classes="btn-primary btn button-medium upload-boundries"
            />
            <a
              href={require("../../../assets/static/serviceAreaUpdate.csv")}
              target="_blank"
              className="btn-primary-outline button-large btn btn-secondary no-border-btn"
            >
              Service Area Sample File
            </a>
          </Col>
          <Col xs="12"></Col>
        </Row>
      </Modal>
      <Modal
        modalHeading="Add a batch of zonal config"
        showModal={showInventoryModal}
        modalLoading={props.isZonalConfigUploading}
        isSuccess={props.isZonalConfigUploaded}
        showProgressString={props.isZonalConfigUploadingError ? false : true}
        failedRecords={props.isZonalConfigUploadingError ? "" : ""}
        closeModalHandler={closeModal}
      >
        {props.isZonalConfigUploadingError ? (
          <div>
            <section className="upload-error">
              <img src={warning} />
              <div className="title">Upload failed</div>
            </section>

            {props.isZonalConfigUploadingError && (
              <div className="desc">
                {props.error && (props.error! as any).errors}
              </div>
            )}
          </div>
        ) : null}
      </Modal>
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>
      <section className="relative container sticky-top">
        <section className="row">
          <section className="col-8">
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <img src={searchicon} alt="close" />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                className="search-input"
                placeholder="Search by city name or ID"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
              <div className="input-group-append">
                <SearchableSelect
                  id="title"
                  isSearchable={true}
                  options={props.citiesList}
                  selectedValue={selectedCity}
                  onChange={handleSelectedCityChange}
                />
              </div>
              <div
                className={isSearch ? "close-button" : "hide close-button"}
                onClick={closeSearch}
              >
                <img src={close} alt="close" />
              </div>
            </InputGroup>
          </section>
          <section className="col-2">
            <Can I="update" a="zone">
              <ButtonComponent
                buttonClass="btn-primary-outline button-medium addBatchBtn"
                buttonText="Add a batch"
                onClickHandler={addBatchModal}
              ></ButtonComponent>
            </Can>
          </section>
          <section className="col-2">
            <Can I="update" a="zone">
              <ButtonComponent
                buttonText="Add a zone"
                onClickHandler={() =>
                  props.addUpdatePageHandler(
                    selectedCity,
                    false,
                    null,
                    searchTerm,
                    window.pageYOffset
                  )
                }
                buttonClass="btn-primary btn btn-secondary button-medium"
              />
            </Can>
          </section>
        </section>
      </section>
      <Can I="read" a="zone">
        <DataTable
          tableId="zonalConfigs-list"
          tableHeaderData={headerData}
          tableFooterData={footerData}
          tableBodyDataTypes={bodyDataAndKeyTypes}
          tableBodyRowData={zonalConfigsData}
          noSearchResultFlag={isSearch}
          editRowHandler={editZonalConfig}
        />
        {/* {isLoadMore && (
          <div id="load-more-container">
            <ButtonComponent
              buttonText={loadMoreText.text}
              buttonClass="btn-primary-outline button-medium"
              onClickHandler={loadFunc}
              isDisable={loadMoreText.isLoading}
            />
          </div>
        )} */}
      </Can>
    </div>
  );
};

export default ZonalConfigsList;
